import React, { useMemo, useRef } from 'react';
import { Box, Typography, Card, CardContent, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const DestinationsSection = React.memo(() => {
  const { i18n } = useTranslation();
  const scrollContainerRef = useRef(null);

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      const newScrollPosition = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const destinationsContent = useMemo(() => ({
    fr: {
      title: 'Nos Zones de Service',
      subtitle: 'Allô Driver vous accompagne dans vos déplacements professionnels et personnels à Montauban et dans toute la région Occitanie.',
      destinations: [
        {
          name: 'Toulouse',
          desc: 'Capitale de la région Occitanie, Toulouse est notre zone de couverture principale. Nos chauffeurs VTC professionnels vous transportent dans toute la ville rose et ses environs, 24h/24 et 7j/7.',
          image: 'https://images.squarespace-cdn.com/content/v1/649b7571aff97962bd746aef/7dee3bd5-c65a-4c75-8cb0-66ef605e5908/What+to+do+in+Toulouse+-+Sunset+from+La+Daurade.jpg',
          popular: true
        },
        {
          name: 'Blagnac',
          desc: 'Service premium vers l\'aéroport de Toulouse-Blagnac. Transferts aéroportuaires ponctuels et confortables, avec suivi de vol et accueil personnalisé.',
          image: 'https://uploads.lebonbon.fr/source/2025/july/2080179/a-roport-toulouse-blagnac_1_2000.jpg',
          popular: true
        },
        {
          name: 'Montauban',
          desc: 'Extension de notre service vers Montauban et ses alentours. Profitez du même niveau de qualité et de ponctualité pour tous vos déplacements dans le Tarn-et-Garonne.',
          image: 'https://lopinion.com/storage/articles/e4CdO0pB5twU7PtvuCxjlmB33AuyOsqSXWZkqbmf.webp',
          popular: true
        },
        {
          name: 'Colomiers',
          desc: 'Service VTC à Colomiers et ses environs. Transport professionnel et ponctuel pour tous vos déplacements dans cette ville dynamique de l\'agglomération toulousaine.',
          image: 'https://images.ladepeche.fr/api/v1/images/view/620dcc7e3e454616e22595b2/large/image.jpg?v=1',
          popular: false
        },
        {
          name: 'Balma',
          desc: 'Déplacements VTC à Balma. Profitez d\'un service de qualité pour tous vos trajets dans cette commune de la métropole toulousaine.',
          image: 'https://www.mairie-balma.fr/wp-content/uploads/2023/12/Balma2-0x0-c-default.png',
          popular: false
        },
        {
          name: 'L\'Union',
          desc: 'Service de chauffeur privé à L\'Union. Transport confortable et fiable dans toute la commune et ses alentours.',
          image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQshl0OfSDefyfdujX9rZKpJYfW8_XZepEN9A&s',
          popular: false
        }
      ]
    },
    en: {
      title: 'Our Service Areas',
      subtitle: 'Allô Driver supports you in your business and personal travels in Montauban and throughout the Occitanie region.',
      destinations: [
        {
          name: 'Toulouse',
          desc: 'Capital of the Occitanie region, Toulouse is our main coverage area. Our professional VTC drivers transport you throughout the pink city and its surroundings, 24/7.',
          image: 'https://images.squarespace-cdn.com/content/v1/649b7571aff97962bd746aef/7dee3bd5-c65a-4c75-8cb0-66ef605e5908/What+to+do+in+Toulouse+-+Sunset+from+La+Daurade.jpg',
          popular: true
        },
        {
          name: 'Blagnac',
          desc: 'Premium service to Toulouse-Blagnac airport. Punctual and comfortable airport transfers, with flight tracking and personalized welcome.',
          image: 'https://uploads.lebonbon.fr/source/2025/july/2080179/a-roport-toulouse-blagnac_1_2000.jpg',
          popular: true
        },
        {
          name: 'Montauban',
          desc: 'Extension of our service to Montauban and its surroundings. Enjoy the same level of quality and punctuality for all your trips in Tarn-et-Garonne.',
          image: 'https://lopinion.com/storage/articles/e4CdO0pB5twU7PtvuCxjlmB33AuyOsqSXWZkqbmf.webp',
          popular: true
        },
        {
          name: 'Colomiers',
          desc: 'VTC service in Colomiers and its surroundings. Professional and punctual transportation for all your trips in this dynamic city of the Toulouse metropolitan area.',
          image: 'https://images.ladepeche.fr/api/v1/images/view/620dcc7e3e454616e22595b2/large/image.jpg?v=1',
          popular: false
        },
        {
          name: 'Balma',
          desc: 'VTC transportation in Balma. Enjoy quality service for all your trips in this commune of the Toulouse metropolis.',
          image: 'https://www.mairie-balma.fr/wp-content/uploads/2023/12/Balma2-0x0-c-default.png',
          popular: false
        },
        {
          name: 'L\'Union',
          desc: 'Private driver service in L\'Union. Comfortable and reliable transportation throughout the town and its surroundings.',
          image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQshl0OfSDefyfdujX9rZKpJYfW8_XZepEN9A&s',
          popular: false
        }
      ]
    }
  }), []);

  const content = useMemo(() => 
    destinationsContent[langCode] || destinationsContent.fr,
    [destinationsContent, langCode]
  );

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      backgroundColor: '#0a0a0a',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{ maxWidth: '1400px', margin: '0 auto', px: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: { xs: '32px', md: '48px' },
              color: '#fff',
              mb: 2
            }}
          >
            {content.title}
          </Typography>
          <Typography 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '16px', md: '18px' },
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            {content.subtitle}
          </Typography>
        </Box>

        {/* Navigation Buttons - Desktop Only */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'center',
          gap: 2,
          mb: 4
        }}>
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              backgroundColor: colors.Button_Primary,
              color: '#000',
              width: 48,
              height: 48,
              '&:hover': {
                backgroundColor: '#e5c158',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              backgroundColor: colors.Button_Primary,
              color: '#000',
              width: 48,
              height: 48,
              '&:hover': {
                backgroundColor: '#e5c158',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        {/* Horizontal Scrollable Container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: 3,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollBehavior: 'smooth',
            pb: 3,
            px: { xs: 0, md: 2 },
            // Hide scrollbar but keep functionality
            '&::-webkit-scrollbar': {
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.Button_Primary,
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: '#e5c158'
              }
            },
            // Smooth scroll on touch devices
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {content.destinations.map((destination, index) => (
            <Card 
              key={index}
              sx={{ 
                minWidth: { xs: '280px', sm: '340px', md: '380px' },
                maxWidth: { xs: '280px', sm: '340px', md: '380px' },
                flexShrink: 0,
                backgroundColor: 'transparent',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                border: destination.popular 
                  ? `3px solid ${colors.Button_Primary}` 
                  : '3px solid rgba(255,255,255,0.15)',
                boxShadow: destination.popular
                  ? `0 12px 40px rgba(212, 175, 55, 0.25)`
                  : '0 8px 30px rgba(0,0,0,0.3)',
                '&:hover': {
                  transform: 'translateY(-16px) scale(1.03)',
                  boxShadow: `0 30px 70px rgba(212, 175, 55, 0.4)`,
                  borderColor: colors.Button_Primary,
                  borderWidth: '3px',
                  '& .destination-overlay': {
                    backgroundColor: 'rgba(0,0,0,0.4)'
                  },
                  '& .destination-image': {
                    transform: 'scale(1.15)'
                  },
                  '& .destination-badge': {
                    transform: 'scale(1.1) rotate(2deg)'
                  }
                }
              }}
            >
              {/* Image */}
              <Box sx={{ 
                position: 'relative',
                height: { xs: 280, sm: 320, md: 380 },
                overflow: 'hidden'
              }}>
                <Box
                  className="destination-image"
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${destination.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                />
                
                {/* Overlay */}
                <Box 
                  className="destination-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.75) 100%)',
                    transition: 'background-color 0.4s ease'
                  }}
                />

                {/* Popular Badge */}
                {destination.popular && (
                  <Box 
                    className="destination-badge"
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      backgroundColor: colors.Button_Primary,
                      color: '#000',
                      px: 2.5,
                      py: 1,
                      borderRadius: '24px',
                      fontSize: '13px',
                      fontWeight: 800,
                      fontFamily: FONT_FAMILY,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    Populaire
                  </Box>
                )}
              </Box>

              {/* Content */}
              <CardContent sx={{ 
                p: { xs: 3, md: 4 },
                backgroundColor: 'rgba(0,0,0,0.95)',
                position: 'relative',
                minHeight: { xs: '140px', md: '160px' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationOnIcon sx={{ 
                    color: colors.Button_Primary, 
                    fontSize: { xs: 28, md: 32 },
                    mt: 0.5,
                    filter: 'drop-shadow(0 2px 8px rgba(212, 175, 55, 0.3))'
                  }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        fontWeight: 800,
                        color: '#fff',
                        mb: 1.5,
                        fontSize: { xs: '20px', md: '24px' },
                        letterSpacing: '0.5px'
                      }}
                    >
                      {destination.name}
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        color: 'rgba(255,255,255,0.8)',
                        fontSize: { xs: '14px', md: '15px' },
                        lineHeight: 1.7,
                        fontWeight: 400
                      }}
                    >
                      {destination.desc}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
});

export default DestinationsSection;
